/** Mobile browser policy. Never cancel menu touches: the lobby and dialogs are
 * native scroll containers. The gameplay canvas/HUD alone own their gestures. */
export function installMobileUI({ app, modalRoot, window: win = window, document: doc = document }) {
  const removers = [];
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    removers.push(() => target.removeEventListener(type, handler, options));
  };
  const editable = el => !!el?.closest?.('input, textarea, select, [contenteditable="true"]');
  const playing = () => app.classList.contains('screen-match') && !modalRoot.firstElementChild;
  const onPlaySurface = event => playing() && !editable(event.target)
    && (event.target === doc || event.target === doc.body
      || !!event.target?.closest?.('#game-canvas, #labels, #app.screen-match'));
  const suppressPlayGesture = event => {
    if (onPlaySurface(event) && event.cancelable) event.preventDefault();
  };
  // Safari gesture events cover its native pinch recognizer. touch-action handles
  // normal Pointer Events; this fallback never disables scrolling in a dialog.
  for (const type of ['gesturestart', 'gesturechange', 'gestureend', 'dblclick', 'contextmenu', 'selectstart', 'dragstart']) {
    listen(doc, type, suppressPlayGesture, { capture: true, passive: false });
  }
  let frame = 0;
  const updateViewport = () => {
    frame = 0;
    const view = win.visualViewport;
    const focused = modalRoot.contains(doc.activeElement) && editable(doc.activeElement);
    // Follow the *visible* area above the keyboard, including Safari's focus pan.
    // Do not counteract an accessibility pinch zoom by resizing the overlay.
    const follow = focused && view && Math.abs(view.scale - 1) < .05;
    const style = modalRoot.style;
    for (const [name, value] of Object.entries({
      '--modal-height': follow ? `${view.height}px` : null,
      '--modal-width': follow ? `${view.width}px` : null,
      '--modal-top': follow ? `${view.offsetTop}px` : null,
      '--modal-left': follow ? `${view.offsetLeft}px` : null
    })) {
      if (value === null) style.removeProperty(name); else style.setProperty(name, value);
    }
    doc.body.toggleAttribute('data-keyboard-open', !!(follow && view.height < win.innerHeight - 100));
    const field = doc.activeElement, modal = field?.closest?.('.modal');
    if (focused && modal) {
      const r = field.getBoundingClientRect(), box = modal.getBoundingClientRect();
      const header = modal.querySelector('header')?.getBoundingClientRect();
      const top = Math.max(box.top + 12, (header?.bottom || box.top) + 12);
      if (r.bottom > box.bottom - 16) modal.scrollTop += r.bottom - box.bottom + 16;
      else if (r.top < top) modal.scrollTop -= top - r.top;
    }
  };
  const schedule = () => { if (!frame) frame = win.requestAnimationFrame(updateViewport); };
  listen(win, 'resize', schedule, { passive: true });
  listen(win, 'orientationchange', schedule, { passive: true });
  if (win.visualViewport) {
    listen(win.visualViewport, 'resize', schedule, { passive: true });
    listen(win.visualViewport, 'scroll', schedule, { passive: true });
  }
  listen(doc, 'focusin', schedule);
  listen(doc, 'focusout', schedule);
  schedule();
  return () => {
    for (const remove of removers) remove();
    if (frame) win.cancelAnimationFrame(frame);
    for (const name of ['height', 'width', 'top', 'left']) modalRoot.style.removeProperty(`--modal-${name}`);
    doc.body.removeAttribute('data-keyboard-open');
  };
}
